import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
} from '@angular/core'
import {DOCUMENT, isPlatformBrowser} from '@angular/common'

const OBSERVER_MARGIN = '-250px'

@Component({
  selector: 'bgo-article-inline-table-of-content',
  templateUrl: './article-inline-table-of-content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
    }
  `,
  standalone: false,
})
export class ArticleInlineTableOfContentComponent implements OnDestroy {
  @Input({required: true}) tableOfContent!: TableOfContent
  @Output() itemSelected = new EventEmitter<{id: string; scrollOffset: number}>()

  private readonly mobileScrollOffset = -150
  private readonly desktopScrollOffset = -100
  protected currentItemIndex?: number
  private observer?: IntersectionObserver

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private platformId: Record<string, unknown>,
    private readonly cdr: ChangeDetectorRef,
  ) {
    afterNextRender(() => this.observeMobileScroll())
  }

  ngOnDestroy(): void {
    this.observer?.disconnect()
    this.observer = undefined
  }

  tocSelectorChanged(event: Event) {
    const target = event.target as HTMLInputElement
    if (target) {
      this.itemSelected.emit({id: target.value, scrollOffset: this.mobileScrollOffset})
      target.blur() // remove focus as this is more a selector than an input
    }
  }

  tocElementClicked(id: string) {
    this.itemSelected.emit({id, scrollOffset: this.desktopScrollOffset})
  }

  private observeMobileScroll() {
    if (!isPlatformBrowser(this.platformId)) {
      return
    }

    let tocIds: string[] = []

    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            this.currentItemIndex = tocIds.indexOf(e.target.id)
            this.cdr.markForCheck()
          }
        })
      },
      {rootMargin: OBSERVER_MARGIN},
    )

    tocIds = this.tableOfContent.map(t => t.id)
    const anchors = this.document.querySelectorAll(tocIds.map(id => `#${id}`).join(','))
    anchors.forEach(a => this.observer?.observe(a))
  }
}

export type TableOfContent = {id: string; title: string}[]
